import {
  createContext,
  type KeyboardEvent,
  type PropsWithChildren,
  type SyntheticEvent,
  useContext,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { getMembershipConversionPath } from '@/utils/membership-access';
import { isAppWebViewState } from '@/store/atom';
import { cn } from '@/utils/tailwind';

import { MembershipConversionOverlay } from './MembershipConversionOverlay';

type MembershipPreviewContextValue = {
  isPreviewMode: boolean;
};

const MembershipPreviewContext = createContext<MembershipPreviewContextValue>({
  isPreviewMode: false,
});

const MEMBERSHIP_PREVIEW_INTERACTIVE_SELECTOR = '[data-membership-preview-interactive="true"]';

const isMembershipPreviewInteractiveTarget = (target: EventTarget | null) => {
  return (
    target instanceof Element && Boolean(target.closest(MEMBERSHIP_PREVIEW_INTERACTIVE_SELECTOR))
  );
};

type MembershipContentLockProps = PropsWithChildren<{
  isLocked: boolean;
}>;

export const MembershipContentLock = ({ children, isLocked }: MembershipContentLockProps) => {
  const location = useLocation();
  const isAppWebView = useRecoilValue(isAppWebViewState);
  const membershipPath = getMembershipConversionPath(location.pathname);
  const contextValue = useMemo(() => ({ isPreviewMode: isLocked }), [isLocked]);

  const blockLockedInteraction = (event: SyntheticEvent<HTMLElement>) => {
    if (!isLocked || isMembershipPreviewInteractiveTarget(event.target)) return;

    event.preventDefault();
    event.stopPropagation();
  };

  const blockLockedKeyboardInteraction = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    blockLockedInteraction(event);
  };

  return (
    <MembershipPreviewContext.Provider value={contextValue}>
      <div className="relative flex min-h-full w-full flex-1">
        <div
          aria-disabled={isLocked}
          className={cn('min-h-full w-full flex-1', isLocked && 'select-none')}
          onClickCapture={blockLockedInteraction}
          onKeyDownCapture={blockLockedKeyboardInteraction}
          onMouseDownCapture={blockLockedInteraction}
          onSubmitCapture={blockLockedInteraction}
        >
          {children}
        </div>
        {isLocked && !isAppWebView && (
          <MembershipConversionOverlay membershipPath={membershipPath} />
        )}
      </div>
    </MembershipPreviewContext.Provider>
  );
};

export const useMembershipPreviewMode = () => {
  return useContext(MembershipPreviewContext).isPreviewMode;
};
